import { css } from 'lit';

export const styles = css`
  :host {
    display: inline-block;
    position: relative; /* contains the absolutely-positioned visually-hidden input */
  }

  [hidden] {
    display: none !important;
  }

  /* ═══════════════════════════════════════════════════════════
     VISUALLY HIDDEN NATIVE INPUT
     Still in the accessibility tree; provides semantics to AT.
     ═══════════════════════════════════════════════════════════ */

  [part='input'] {
    appearance: none;
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  [part='input']:focus-visible {
    outline: none;
  }

  /* ═══════════════════════════════════════════════════════════
     ROOT WRAPPER
     ═══════════════════════════════════════════════════════════ */

  [part='root'] {
    display: inline-flex;
    flex-direction: row;
    align-items: flex-start;
    gap: var(--asc-checkbox-gap, var(--asc-spacing-sm));
    cursor: pointer;
  }

  :host([disabled]) [part='root'] {
    cursor: not-allowed;
  }

  /* ═══════════════════════════════════════════════════════════
     CUSTOM INDICATOR
     ═══════════════════════════════════════════════════════════ */

  [part='control'] {
    /* Icons are position:absolute inside this element, so no flex centering needed.
       Using display:block + overflow:hidden ensures the control's layout footprint
       is always exactly width × height, regardless of which icon is visible. */
    position: relative;
    flex-shrink: 0;
    display: block;
    box-sizing: border-box;
    overflow: hidden;

    /* ── Size: sm (default) ──────────────────────────────── */
    width: var(--asc-checkbox-size-sm, 16px);
    height: var(--asc-checkbox-size-sm, 16px);
    border-radius: var(--asc-checkbox-radius-sm, var(--asc-radius-xs));

    border: 1px solid var(--asc-checkbox-border-color, var(--asc-border-primary));
    background: transparent;

    transition: background-color 100ms ease, border-color 100ms ease;
  }

  /* ── Size: md ─────────────────────────────────────────── */
  :host([size='md']) [part='control'] {
    width: var(--asc-checkbox-size-md, 20px);
    height: var(--asc-checkbox-size-md, 20px);
    border-radius: var(--asc-checkbox-radius-md, var(--asc-radius-sm));
  }

  /* ── Radio shape ──────────────────────────────────────── */
  :host([type='radio']) [part='control'] {
    border-radius: var(--asc-checkbox-radio-radius, var(--asc-radius-full));
  }

  /* ── Checked & Indeterminate ──────────────────────────── */
  :host([checked]) [part='control'],
  :host([indeterminate]) [part='control'] {
    background: var(--asc-checkbox-checked-bg, var(--asc-bg-brand-solid));
    border-color: var(--asc-checkbox-checked-bg, var(--asc-bg-brand-solid));
  }

  /* ── Checked hover ────────────────────────────────────── */
  :host([checked]:not([disabled])) [part='root']:hover [part='control'],
  :host([indeterminate]:not([disabled])) [part='root']:hover [part='control'] {
    background: var(--asc-checkbox-checked-bg-hover, var(--asc-bg-brand-solid_hover));
    border-color: var(--asc-checkbox-checked-bg-hover, var(--asc-bg-brand-solid_hover));
  }

  /* ── Disabled ─────────────────────────────────────────── */
  :host([disabled]) [part='control'] {
    background: var(--asc-checkbox-disabled-bg, var(--asc-bg-disabled_subtle));
    border-color: var(--asc-checkbox-disabled-border, var(--asc-border-disabled));
  }

  /* ── Focus ring via :has() ────────────────────────────── */
  :host:has([part='input']:focus-visible) [part='control'] {
    box-shadow:
      0 0 0 2px var(--asc-checkbox-focus-ring-gap-color, var(--asc-bg-primary)),
      0 0 0 4px var(--asc-checkbox-focus-ring-color, var(--asc-focus-ring));
  }

  /* ═══════════════════════════════════════════════════════════
     INNER ICONS
     All icons are position:absolute so toggling display:none ↔ block
     never participates in the control's layout and cannot cause a shift.
     ═══════════════════════════════════════════════════════════ */

  [part='check-icon'],
  [part='dash-icon'],
  [part='dot'] {
    display: none;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  /* Checkbox: checked (no indeterminate) → check icon */
  :host([type='checkbox'][checked]:not([indeterminate])) [part='check-icon'] {
    display: block;
  }

  /* Checkbox: indeterminate → dash icon */
  :host([type='checkbox'][indeterminate]) [part='dash-icon'] {
    display: block;
  }

  /* Radio: checked → dot */
  :host([type='radio'][checked]) [part='dot'] {
    display: block;
  }

  /* SVG icons — sized at 75% of the indicator */
  [part='check-icon'],
  [part='dash-icon'] {
    width: 75%;
    height: 75%;
    color: var(--asc-checkbox-icon-color, var(--asc-fg-white));
  }

  :host([disabled]) [part='check-icon'],
  :host([disabled]) [part='dash-icon'] {
    color: var(--asc-checkbox-disabled-icon-color, var(--asc-fg-disabled_subtle));
  }

  /* Radio inner dot — 37.5% of indicator (matches Figma inset geometry) */
  [part='dot'] {
    width: 37.5%;
    height: 37.5%;
    border-radius: var(--asc-radius-full);
    background: var(--asc-checkbox-icon-color, var(--asc-fg-white));
  }

  :host([disabled]) [part='dot'] {
    background: var(--asc-checkbox-disabled-icon-color, var(--asc-fg-disabled_subtle));
  }

  /* ═══════════════════════════════════════════════════════════
     LABEL GROUP
     ═══════════════════════════════════════════════════════════ */

  [part='label-group'] {
    display: flex;
    flex-direction: column;
    gap: var(--asc-spacing-xs);
  }

  [part='label'] {
    display: block;
    color: var(--asc-checkbox-label-color, var(--asc-text-secondary));
    font-size: var(--asc-checkbox-label-font-size-sm, var(--asc-font-size-text-sm));
    font-weight: var(--asc-checkbox-label-font-weight, var(--asc-font-weight-medium));
    line-height: var(--asc-line-height-text-sm);
  }

  :host([size='md']) [part='label'] {
    font-size: var(--asc-checkbox-label-font-size-md, var(--asc-font-size-text-md));
    line-height: var(--asc-line-height-text-md);
  }

  [part='supporting-text'] {
    display: block;
    color: var(--asc-checkbox-supporting-text-color, var(--asc-text-tertiary));
    font-size: var(--asc-checkbox-supporting-text-font-size-sm, var(--asc-font-size-text-sm));
    font-weight: var(--asc-checkbox-supporting-text-font-weight, var(--asc-font-weight-regular));
    line-height: var(--asc-line-height-text-sm);
  }

  :host([size='md']) [part='supporting-text'] {
    font-size: var(--asc-checkbox-supporting-text-font-size-md, var(--asc-font-size-text-md));
    line-height: var(--asc-line-height-text-md);
  }

  /* ── Disabled text ────────────────────────────────────── */
  :host([disabled]) [part='label'],
  :host([disabled]) [part='supporting-text'] {
    color: var(--asc-checkbox-disabled-label-color, var(--asc-fg-disabled));
  }
`;
